import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';

import { Textinput } from '@stratumn/atomic';

import isHotkey from 'is-hotkey';
import compose from 'lodash.flowright';
import { ErrorBoundary } from 'react-error-boundary';
import {
  createEditor,
  type Descendant,
  Editor,
  Range,
  Text,
  Transforms
} from 'slate';
import { withHistory } from 'slate-history';
import {
  Editable,
  type RenderElementProps,
  Slate,
  withReact
} from 'slate-react';

import { cn } from '@/shadcn/lib/utils';

import { Element } from '@/components/ui/richEditor/elements/element.richEditor';
import { Leaf } from '@/components/ui/richEditor/leaf/leaf.richEditor';
import { SubjectAutoComplete } from '@/components/ui/richEditor/subjectList/subjectList.richEditor';
import { Toolbar } from '@/components/ui/richEditor/toolbar/toolbar.richEditor';
import { UserList } from '@/components/ui/richEditor/userList/userList.richEditor';
import { HOTKEYS } from './constants.richEditor';
import {
  getWithTables,
  isTableActive,
  onTableKeydown,
  onTableMouseDown
} from './tables/table.utils.richEditor';
import {
  defaultEmptyValue,
  focusEditor,
  getWithSubjectLine,
  insertMention,
  insertSubject,
  isRichEditorEmpty,
  isSubjectActive,
  parseValue,
  toggleMark,
  withImages,
  withIsEmptyAttribute,
  withLinks,
  withMentionsPlugin,
  withSpacesPreserved
} from './utils';

type Props = {
  className?: string;
  placeholder?: string;
  subjectPlaceholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  value?: string;
  autoFocus?: boolean;
  withSubject?: boolean;
  withMentions?: boolean;
  subjectRequired?: boolean;
  workflowContext?: any;
  searchValue?: string;
  dataCy?: string;
  id?: string;
};

export type RichEditorRef = {
  clearEditor: () => void;
};

export const RichEditorInner = forwardRef<RichEditorRef | undefined, Props>(
  (
    {
      className = '',
      placeholder = 'Enter a comment here.',
      subjectPlaceholder = 'Subject',
      disabled = false,
      onChange = () => {},
      onBlur = () => {},
      value,
      autoFocus = false,
      withSubject = false,
      withMentions = false,
      subjectRequired = false,
      readOnly = false,
      workflowContext = {},
      searchValue = '',
      dataCy,
      id,
      ...props
    },
    ref
  ) => {
    // This state variable will be used to track if the editor has been initiated
    // i.e. after the editor was normalized on its first render (see useEffect below)
    const [editorInitiated, setEditorInitiated] = useState<boolean>(false);

    const renderElement = useCallback(
      (elementProps: RenderElementProps) => (
        <Element
          dataCy={dataCy}
          editorInitiated={editorInitiated}
          placeholder={placeholder}
          readOnly={readOnly}
          subjectPlaceholder={subjectPlaceholder}
          subjectRequired={subjectRequired}
          withMentions={withMentions}
          withSubject={withSubject}
          {...elementProps}
        />
      ),
      [editorInitiated, withSubject, withMentions, subjectRequired, readOnly]
    );

    const renderLeaf = useCallback(leafProps => <Leaf {...leafProps} />, []);

    const withTables = getWithTables(readOnly);
    const withSubjectLine = getWithSubjectLine(withSubject, readOnly);

    const editor = useMemo(
      () =>
        compose(
          withImages,
          withTables,
          withSubjectLine,
          withLinks,
          withMentionsPlugin,
          withSpacesPreserved,
          withReact,
          withHistory,
          withIsEmptyAttribute
        )(createEditor()),
      []
    );

    const [isFocused, setIsFocused] = useState(false);

    const [editorValue, setEditorValue] = useState<Descendant[]>(() =>
      parseValue(value)
    );
    const [target, setTarget] = useState<Range | null>();
    const [index, setIndex] = useState(0);
    const [search, setSearch] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [subjectSearch, setSubjectSearch] = useState('');
    const users =
      withMentions &&
      workflowContext?.members
        ?.filter(c => c.name.toLowerCase().startsWith(search.toLowerCase()))
        .slice(0, 10);
    const subjectsList = workflowContext?.subjects;
    const [subjects, setSubjects] = useState(
      subjectSearch?.length
        ? subjectsList
            ?.filter(sub =>
              sub.toLowerCase().startsWith(subjectSearch.toLowerCase())
            )
            .slice(0, 10)
        : ''
    );

    useImperativeHandle(ref, () => ({
      clearEditor() {
        const point = { path: [0, 0], offset: 0 };
        editor.selection = { anchor: point, focus: point };
        editor.history = { redos: [], undos: [] };
        editor.children = JSON.parse(defaultEmptyValue);
        Editor.normalize(editor, { force: true });
      }
    }));

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleEditorMouseDown = e => {
      if (e.currentTarget === e.target) {
        e.preventDefault();
        focusEditor(editor);
      }
      onTableMouseDown(editor);
    };

    const handleKeyDown = useCallback(
      event => {
        if (isTableActive(editor)) {
          onTableKeydown(event, editor);
        }
        const { selection } = editor;
        const subjectIsActive = isSubjectActive(editor);
        setIsDirty(true);
        const subjectsAreActive =
          subjectIsActive &&
          (subjects?.length || subjectSearch?.length) &&
          withSubject;
        // Subject Suggestions
        if (event.key === ' ' && event.ctrlKey && withSubject)
          setSubjects(subjectsList.splice(0, 10));
        // Key event actions for mentions and subjects
        const keyEventActions = data => {
          const dataIndex = target ? index : subjectIndex;
          const setDataIndex = dataIndex =>
            target ? setIndex(dataIndex) : setSubjectIndex(dataIndex);
          switch (event.key) {
            case 'ArrowDown': {
              event.preventDefault();
              const prevIndex =
                dataIndex >= data?.length - 1 ? 0 : dataIndex + 1;
              setDataIndex(prevIndex);
              break;
            }
            case 'ArrowUp': {
              event.preventDefault();
              const nextIndex =
                dataIndex <= 0 ? data?.length - 1 : dataIndex - 1;
              setDataIndex(nextIndex);
              break;
            }
            case 'Tab':
            case 'Enter': {
              if (!(target || data)) return;
              if (data[index]) {
                event.preventDefault();
                if (target) {
                  Transforms.select(editor, target);
                  insertMention(editor, data[index]);
                  setTarget(null);
                } else {
                  insertSubject(editor, data[subjectIndex]);
                }
              }
              break;
            }
            case 'Escape':
              event.preventDefault();
              if (target) {
                setTarget(null);
              } else {
                setSubjectSearch('');
              }
              break;
            default:
              break;
          }
        };
        // triggers key events based on mentions or subjects
        if (withMentions && target) {
          keyEventActions(users);
        } else if (subjectsAreActive) {
          keyEventActions(subjects);
        }

        // Right/left events to move inside/outside of a link element
        if (selection && Range.isCollapsed(selection)) {
          const { nativeEvent } = event;
          if (isHotkey('left', nativeEvent)) {
            event.preventDefault();
            Transforms.move(editor, { unit: 'offset', reverse: true });
            return;
          }
          if (isHotkey('right', nativeEvent)) {
            event.preventDefault();
            Transforms.move(editor, { unit: 'offset' });
            return;
          }
        }

        // Hotkeys shortcuts
        if (!subjectIsActive) {
          Object.keys(HOTKEYS).forEach(hotkey => {
            if (isHotkey(hotkey, event)) {
              event.preventDefault();
              const mark = HOTKEYS[hotkey];
              toggleMark(editor, mark);
            }
          });
        }
        // Line break
        if (isHotkey('shift+Enter', event)) {
          event.preventDefault();
          if (!subjectIsActive) {
            editor.insertText('\n');
          }
        }
      },
      [
        editor,
        index,
        subjectIndex,
        subjectSearch?.length,
        subjects,
        subjectsList,
        target,
        users,
        withMentions,
        withSubject
      ]
    );
    const handleChange = value => {
      const { selection } = editor;
      if (
        withSubject &&
        value.find(val => val.type === 'subject')?.children[0]
      ) {
        setSubjectSearch(
          value.find(val => val.type === 'subject')?.children[0]?.text
        );
        setSubjectIndex(0);
      }
      if (users?.length > 0 && selection && Range.isCollapsed(selection)) {
        const [start] = Range.edges(selection);
        const wordBefore = Editor.before(editor, start, { unit: 'word' });
        const before = wordBefore && Editor.before(editor, wordBefore);
        const beforeRange = before && Editor.range(editor, before, start);
        const beforeText = beforeRange && Editor.string(editor, beforeRange);
        const beforeMatch = beforeText?.match(/^@(\w+)$/);

        const after = Editor.after(editor, start);
        const afterRange = Editor.range(editor, start, after);
        const afterText = Editor.string(editor, afterRange);
        const afterMatch = afterText.match(/^(\s|$)/);

        if (beforeMatch && afterMatch) {
          setTarget(beforeRange);
          setSearch(beforeMatch[1]);
          setIndex(0);
          return;
        }
      }

      setTarget(null);
      setEditorValue(value);
    };
    const decorate = useCallback(
      ([node, path]) => {
        const ranges: Range[] = [];

        if (searchValue && Text.isText(node)) {
          const { text } = node;
          const parts = text.toLowerCase().split(searchValue.toLowerCase());
          let offset = 0;

          parts.forEach((part, i) => {
            if (i !== 0) {
              ranges.push({
                anchor: { path, offset: offset - searchValue.length },
                focus: { path, offset },
                highlight: true
              });
            }

            offset = offset + part.length + searchValue.length;
          });
        }
        return ranges;
      },
      [searchValue]
    );

    /**
     * Effect to:
     * - Force normalisation of the value on first render
     * - Allow a falsy value (undefined or empty string) to be updated throug the props so the field is reset
     */
    useEffect(() => {
      if (!editorInitiated || readOnly || !value) {
        // Parse string value to get slate format
        const parsedValue = parseValue(value);
        // Effect to force content normalization
        editor.children = parsedValue;
        Editor.normalize(editor, { force: true });
        // We set the internal value so that the rendering can take over from here
        setEditorValue(editor.children);
        // Set initiated to true
        setEditorInitiated(true);
      }
    }, [value, readOnly, editor]);

    useEffect(() => {
      setSubjects(
        subjectSearch?.length
          ? subjectsList
              ?.filter(sub =>
                sub.toLowerCase().startsWith(subjectSearch.toLowerCase())
              )
              .slice(0, 10)
          : ''
      );
    }, [subjectSearch, subjectsList]);

    useEffect(() => {
      // Set a timeout to update debounced value
      const handler = setTimeout(() => {
        if (isDirty) {
          onChange(
            isRichEditorEmpty(editorValue)
              ? undefined
              : JSON.stringify(editorValue)
          );
        }
      }, 200);
      // Cleanup the timeout if `editorValue` changes before the timeout expires
      return () => {
        clearTimeout(handler);
      };
    }, [editorValue, isDirty]);

    useEffect(() => {
      if (autoFocus) {
        focusEditor(editor);
      }
    }, [autoFocus, editor]);

    return (
      <div
        className="w-full"
        data-cy={`rich-editor${withSubject ? '-with-subject' : ''}`}
        data-is-disabled={disabled}
        data-testid="rich-editor"
        data-with-subject={withSubject}
        id={id}
      >
        <Slate
          editor={editor}
          initialValue={editorValue}
          onChange={handleChange}
        >
          {!readOnly && <Toolbar disabled={disabled || !isFocused} />}
          <div
            className={cn(
              'w-full text-sm',
              !readOnly &&
                'scrollbar-thin max-h-[400px] overflow-auto rounded-sm border border-input p-2.5 text-sm',
              !(readOnly || disabled) && 'cursor-text',
              className
            )}
            data-has-focus={isFocused}
            onMouseDown={handleEditorMouseDown}
          >
            <Editable
              autoFocus={autoFocus}
              className={cn(
                'w-full break-words outline-none',
                !readOnly && 'min-h-[100px] [&>*]:mb-2.5',
                readOnly && '[&>*:not(:last-child)]:mb-2.5'
              )}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              spellCheck
              {...props}
              decorate={decorate}
              readOnly={readOnly || disabled}
            />
          </div>
          {users?.length > 0 && (
            <UserList
              index={index}
              search={search}
              target={target}
              users={users}
            />
          )}
          <SubjectAutoComplete index={subjectIndex} subjects={subjects} />
        </Slate>
      </div>
    );
  }
);

export const RichEditor = forwardRef<RichEditorRef | undefined, Props>(
  (props, ref) => (
    <ErrorBoundary
      fallback={<Textinput type="text" value={props.value || ''} {...props} />}
    >
      <RichEditorInner {...props} ref={ref} />
    </ErrorBoundary>
  )
);
